import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { inr, fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Operations() {
  const [tab, setTab] = useState("procurement");
  return (
    <div className="space-y-5" data-testid="operations-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight">Made-to-Order Operations</h1>
        <p className="text-sm text-neutral-500 mt-1">
          Procurement hedging, weight variance and fulfilment mix for the asset-light model. Prices on orders never change — this screen is about acting on today's metal.
        </p>
      </div>
      <div className="flex gap-2">
        {[["procurement", "Procurement Queue"], ["margins", "Realised Margin"], ["variance", "Weight Variance"], ["fulfilment", "Stock vs Made-to-Order"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm border transition-colors ${tab === k ? "bg-stone-900 text-white border-stone-900" : "bg-white border-neutral-300 hover:border-stone-500"}`}
            data-testid={`ops-tab-${k}`}>{l}</button>
        ))}
      </div>
      {tab === "procurement" && <Procurement />}
      {tab === "margins" && <Margins />}
      {tab === "variance" && <Variance />}
      {tab === "fulfilment" && <Fulfilment />}
    </div>
  );
}

function Procurement() {
  const [data, setData] = useState(null);
  const [rates, setRates] = useState({});
  const [settings, setSettings] = useState(null);

  const load = () => {
    api.get("/admin/procurement").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const procure = async (id) => {
    const rate = parseFloat(rates[id]);
    if (!rate) return toast.error("Enter the actual rate paid");
    try {
      const r = await api.post(`/admin/procurement/${id}/procured`, { rate_paid: rate });
      toast.success(`Procured — realised margin ${inr(r.data.realised_margin)}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveThresholds = async () => {
    try {
      await api.put("/admin/settings", {
        ...settings,
        procurement_alert_hours: Number(settings.procurement_alert_hours ?? 24),
        procurement_alert_move_pct: Number(settings.procurement_alert_move_pct ?? 1.5),
        mto_free_cancel_hours: Number(settings.mto_free_cancel_hours ?? 24),
      });
      toast.success("Thresholds saved");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!data) return <p className="text-sm text-neutral-400 font-mono">LOADING…</p>;
  const s = data.summary;

  return (
    <div className="space-y-5">
      {s.alert && (
        <div className="border-2 border-red-400 bg-red-50 p-4" data-testid="ops-exposure-alert">
          <p className="font-heading font-semibold text-red-700">RATE EXPOSURE ALERT</p>
          <p className="text-sm text-red-700 mt-1">
            {s.unhedged_fine_grams}g fine metal unhedged · {inr(s.unhedged_exposure_inr)} at current rates
            {s.stale_count ? ` · ${s.stale_count} task(s) older than ${s.alert_hours}h` : ""}
            {s.moved_count ? ` · ${s.moved_count} past ±${s.alert_move_pct}% rate move` : ""}
          </p>
        </div>
      )}

      <section className="bg-white border border-neutral-200 p-5" data-testid="buy-today">
        <h2 className="font-heading font-semibold mb-3">Total fine grams to buy today</h2>
        {data.buy_today.length === 0 ? (
          <p className="text-sm text-emerald-700" data-testid="buy-today-clear">Nothing pending — every paid made-to-order line is hedged.</p>
        ) : (
          <div className="flex flex-wrap gap-3">
            {data.buy_today.map((a) => (
              <div key={`${a.metal}-${a.purity}`} className="border border-gold/50 bg-ivory px-5 py-4" data-testid={`buy-${a.metal}-${a.purity}`}>
                <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">{a.metal} {a.purity}</p>
                <p className="font-heading text-3xl font-bold mt-1">{a.fine_grams}<span className="text-sm font-normal text-neutral-400"> g fine</span></p>
                <p className="text-xs text-neutral-500 mt-1">{a.lines} line(s) · live {inr(a.current_rate)}/g</p>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="bg-white border border-neutral-200 p-5" data-testid="procurement-queue">
        <h2 className="font-heading font-semibold mb-3">Pending tasks ({s.pending})</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[1000px]">
            <thead>
              <tr className="text-left text-[10px] font-mono uppercase tracking-widest text-neutral-500 border-b border-neutral-200">
                {["Order", "Design", "Purity", "Billing g", "Fine g", "Locked rate", "Live rate", "Move", "Age", "Exposure", "Rate paid", ""].map((h) => <th key={h} className="py-2 pr-2">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.tasks.map((t) => (
                <tr key={t.id || t._id} className={`border-b border-neutral-100 ${t.alert_age || t.alert_move ? "bg-red-50/50" : ""}`} data-testid={`proc-task-${t.id || t._id}`}>
                  <td className="py-2 pr-2 font-mono">{t.order_number}</td>
                  <td className="py-2 pr-2">{t.title}</td>
                  <td className="py-2 pr-2 font-mono">{t.metal} {t.purity}</td>
                  <td className="py-2 pr-2 font-mono">{t.billing_weight}</td>
                  <td className="py-2 pr-2 font-mono font-semibold">{t.fine_grams}</td>
                  <td className="py-2 pr-2 font-mono">{inr(t.rate_per_gram)}</td>
                  <td className="py-2 pr-2 font-mono">{inr(t.current_rate)}</td>
                  <td className={`py-2 pr-2 font-mono ${Math.abs(t.rate_move_pct) > s.alert_move_pct ? "text-red-600 font-semibold" : ""}`}>{t.rate_move_pct > 0 ? "+" : ""}{t.rate_move_pct}%</td>
                  <td className={`py-2 pr-2 font-mono ${t.alert_age ? "text-red-600 font-semibold" : ""}`}>{t.age_hours}h</td>
                  <td className="py-2 pr-2 font-mono">{t.exposure_inr ? inr(t.exposure_inr) : "—"}</td>
                  <td className="py-2 pr-2"><Input type="number" placeholder="₹/g" value={rates[t.id || t._id] || ""} onChange={(e) => setRates({ ...rates, [t.id || t._id]: e.target.value })} className="h-7 w-24" data-testid={`proc-rate-${t.id || t._id}`} /></td>
                  <td className="py-2"><Button size="sm" variant="outline" onClick={() => procure(t.id || t._id)} data-testid={`proc-done-${t.id || t._id}`}>Procured</Button></td>
                </tr>
              ))}
              {!data.tasks.length && <tr><td colSpan={12} className="py-6 text-center text-neutral-400">Queue is clear.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      {settings && (
        <section className="bg-white border border-neutral-200 p-5 flex flex-wrap items-end gap-4" data-testid="ops-thresholds">
          <div><Label>Alert if unprocured after (hours)</Label><Input type="number" value={settings.procurement_alert_hours ?? 24} onChange={(e) => setSettings({ ...settings, procurement_alert_hours: e.target.value })} className="h-9 w-28" data-testid="ops-alert-hours" /></div>
          <div><Label>Alert on rate move beyond (%)</Label><Input type="number" step="0.1" value={settings.procurement_alert_move_pct ?? 1.5} onChange={(e) => setSettings({ ...settings, procurement_alert_move_pct: e.target.value })} className="h-9 w-28" data-testid="ops-alert-move" /></div>
          <div><Label>MTO free-cancellation window (hours)</Label><Input type="number" value={settings.mto_free_cancel_hours ?? 24} onChange={(e) => setSettings({ ...settings, mto_free_cancel_hours: e.target.value })} className="h-9 w-28" data-testid="ops-cancel-window" /></div>
          <Button size="sm" onClick={saveThresholds} data-testid="ops-save-thresholds">Save thresholds</Button>
        </section>
      )}
    </div>
  );
}

function Margins() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/procurement/margins").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!data) return <p className="text-sm text-neutral-400 font-mono">LOADING…</p>;
  return (
    <section className="bg-white border border-neutral-200 p-5" data-testid="margins-table">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="font-heading font-semibold">Realised margin — customer rate vs rate actually paid</h2>
        <p className={`font-heading text-xl font-bold ${data.total_realised_margin >= 0 ? "text-emerald-700" : "text-red-600"}`} data-testid="total-margin">{inr(data.total_realised_margin)}</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="text-left text-[10px] font-mono uppercase text-neutral-500 border-b">{["Procured", "Order", "Design", "Purity", "Fine g", "Customer rate", "Paid rate", "Margin"].map((h) => <th key={h} className="py-2 pr-2">{h}</th>)}</tr></thead>
          <tbody>
            {data.tasks.map((t) => (
              <tr key={t.id || t._id} className="border-b border-neutral-100" data-testid={`margin-${t.id || t._id}`}>
                <td className="py-1.5 pr-2">{fmtDateTime(t.procured_at)}</td>
                <td className="py-1.5 pr-2 font-mono">{t.order_number}</td>
                <td className="py-1.5 pr-2">{t.title}</td>
                <td className="py-1.5 pr-2 font-mono">{t.purity}</td>
                <td className="py-1.5 pr-2 font-mono">{t.fine_grams}</td>
                <td className="py-1.5 pr-2 font-mono">{inr(t.rate_per_gram)}</td>
                <td className="py-1.5 pr-2 font-mono">{inr(t.procured_rate)}</td>
                <td className={`py-1.5 font-mono font-semibold ${t.realised_margin >= 0 ? "text-emerald-700" : "text-red-600"}`}>{inr(t.realised_margin)}</td>
              </tr>
            ))}
            {!data.tasks.length && <tr><td colSpan={8} className="py-6 text-center text-neutral-400">No procurement completed yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Variance() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/reports/weight-variance").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!data) return <p className="text-sm text-neutral-400 font-mono">LOADING…</p>;
  return (
    <div className="space-y-5">
      <section className="bg-white border border-neutral-200 p-5" data-testid="variance-designs">
        <h2 className="font-heading font-semibold mb-1">Actual vs billing weight, per design</h2>
        <p className="text-xs text-neutral-500 mb-3">Designs running heavy (avg actual &gt; billing) lose money on every order — tune their tolerance. Prices charged never change; this is business intelligence only.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-[10px] font-mono uppercase text-neutral-500 border-b">{["Design", "Title", "Pieces", "Avg billing g", "Avg actual g", "Variance", "₹ impact / piece", ""].map((h) => <th key={h} className="py-2 pr-2">{h}</th>)}</tr></thead>
            <tbody>
              {data.designs.map((d) => (
                <tr key={d.design} className={`border-b border-neutral-100 ${d.runs_heavy ? "bg-red-50/50" : ""}`} data-testid={`variance-${d.design}`}>
                  <td className="py-1.5 pr-2 font-mono">{d.design}</td>
                  <td className="py-1.5 pr-2">{d.title}</td>
                  <td className="py-1.5 pr-2 font-mono">{d.n}</td>
                  <td className="py-1.5 pr-2 font-mono">{d.avg_billing}</td>
                  <td className="py-1.5 pr-2 font-mono">{d.avg_actual}</td>
                  <td className={`py-1.5 pr-2 font-mono ${d.variance_pct > 0 ? "text-red-600" : "text-emerald-700"}`}>{d.variance_pct > 0 ? "+" : ""}{d.variance_pct}%</td>
                  <td className="py-1.5 pr-2 font-mono">{inr(d.rupee_impact_per_piece)}</td>
                  <td className="py-1.5">{d.runs_heavy && <span className="text-[10px] font-mono uppercase text-red-700 border border-red-300 px-1.5 py-0.5">runs heavy</span>}</td>
                </tr>
              ))}
              {!data.designs.length && <tr><td colSpan={8} className="py-6 text-center text-neutral-400">No actual weights recorded yet — production enters them on the order line.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
      <section className="bg-white border border-neutral-200 p-5" data-testid="variance-categories">
        <h2 className="font-heading font-semibold mb-3">Per category</h2>
        <div className="flex flex-wrap gap-2">
          {data.categories.map((c) => (
            <span key={c.category} className="text-xs font-mono border border-neutral-200 px-3 py-2" data-testid={`variance-cat-${c.category}`}>
              {c.category}: {c.n} pieces · {c.variance_pct > 0 ? "+" : ""}{c.variance_pct}%
            </span>
          ))}
          {!data.categories.length && <p className="text-sm text-neutral-400">—</p>}
        </div>
      </section>
    </div>
  );
}

function Fulfilment() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/reports/fulfilment").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!data) return <p className="text-sm text-neutral-400 font-mono">LOADING…</p>;
  return (
    <section className="bg-white border border-neutral-200 p-5" data-testid="fulfilment-report">
      <h2 className="font-heading font-semibold mb-1">Which designs earn a place in stock</h2>
      <p className="text-xs text-neutral-500 mb-3">Ranked by order frequency. Designs with 3+ orders still fully made-to-order are deliberate stock candidates.</p>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="text-left text-[10px] font-mono uppercase text-neutral-500 border-b">{["Design", "Title", "Orders", "Revenue", "MTO purities", ""].map((h) => <th key={h} className="py-2 pr-2">{h}</th>)}</tr></thead>
          <tbody>
            {data.designs.map((d) => (
              <tr key={d.design} className={`border-b border-neutral-100 ${d.promote_candidate ? "bg-amber-50/60" : ""}`} data-testid={`fulfil-${d.design}`}>
                <td className="py-1.5 pr-2 font-mono">{d.design}</td>
                <td className="py-1.5 pr-2">{d.title}</td>
                <td className="py-1.5 pr-2 font-mono">{d.orders}</td>
                <td className="py-1.5 pr-2 font-mono">{inr(d.revenue)}</td>
                <td className="py-1.5 pr-2 font-mono">{d.mto_purities.join(", ") || "—"}</td>
                <td className="py-1.5">{d.promote_candidate && <span className="text-[10px] font-mono uppercase text-amber-700 border border-amber-300 px-1.5 py-0.5">promote to stock?</span>}</td>
              </tr>
            ))}
            {!data.designs.length && <tr><td colSpan={6} className="py-6 text-center text-neutral-400">No orders yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}
